import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { buscarTimbrado, consultarContribuyente, crearComprobante, registrarTimbrado } from '../services/api';
import { useAuthValidation } from '../hooks/useAuthValidation';
import { DateInput } from '../utils/dateUtils';

const INITIAL_FORM_DATA = {
  ruc: '',
  numero_timbrado: '',
  nombre_razon_social: '',
  tipo_comprobante: '109',
  fecha_comprobante: '',
  numero_comprobante: '',
  monto_gravado_10: 0,
  monto_gravado_5: 0,
  monto_exento: 0,
  monto_total: 0,
  imputa_iva: 'S',
  imputa_ire: 'N',
  imputa_irp_rsp: 'S',
  condicion_operacion: '1',
  moneda_extranjera: 'N',
  fecha_inicio_vigencia: '',
  fecha_fin_vigencia: '',
  tipo_registro: '2',
};

const ComprobanteForm = () => {
  useAuthValidation();
  const navigate = useNavigate();
  const rucInputRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [timbradoInfo, setTimbradoInfo] = useState(null);
  const [timbradoOptions, setTimbradoOptions] = useState(null);

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);

  const [showTimbradoForm, setShowTimbradoForm] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Convert razon social to uppercase
    if (name === 'nombre_razon_social') {
      setFormData(prev => ({
        ...prev,
        [name]: value.toUpperCase(),
      }));
      return;
    }
    
    // Auto-format numero_comprobante with dashes
    if (name === 'numero_comprobante') {
      const cleaned = value.replace(/\D/g, '');
      let formatted = cleaned;
      if (cleaned.length > 3) {
        formatted = cleaned.slice(0, 3) + '-' + cleaned.slice(3);
      }
      if (cleaned.length > 6) {
        formatted = cleaned.slice(0, 3) + '-' + cleaned.slice(3, 6) + '-' + cleaned.slice(6, 13);
      }
      setFormData(prev => ({
        ...prev,
        [name]: formatted,
      }));
      return;
    }
    
    // Auto-calculate fecha_fin_vigencia (last day of the month, 1 year after fecha_inicio)
    if (name === 'fecha_inicio_vigencia' && value) {
      // Value is in DD/MM/YYYY format
      const [day, month, year] = value.split('/');
      const inicioDate = new Date(year, month - 1, day);
      const finYear = inicioDate.getFullYear() + 1;
      const finMonthIndex = inicioDate.getMonth();
      const finDate = new Date(finYear, finMonthIndex + 1, 0); // last day of that month

      const finDay = String(finDate.getDate()).padStart(2, '0');
      const finMonth = String(finDate.getMonth() + 1).padStart(2, '0');
      const finFormatted = `${finDay}/${finMonth}/${finYear}`;
      
      setFormData(prev => ({
        ...prev,
        [name]: value,
        fecha_fin_vigencia: finFormatted,
      }));
      return;
    }
    
    // Auto-calculate total
    if (['monto_gravado_10', 'monto_gravado_5', 'monto_exento'].includes(name)) {
      setFormData(prev => {
        const g10 = name === 'monto_gravado_10' ? parseInt(value) || 0 : parseInt(prev.monto_gravado_10) || 0;
        const g5 = name === 'monto_gravado_5' ? parseInt(value) || 0 : parseInt(prev.monto_gravado_5) || 0;
        const exento = name === 'monto_exento' ? parseInt(value) || 0 : parseInt(prev.monto_exento) || 0;
        return {
          ...prev,
          [name]: value,
          monto_total: g10 + g5 + exento,
        };
      });
      return;
    }

    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleBuscarTimbrado = async () => {
    if (!formData.ruc || !formData.fecha_comprobante) {
      toast.error('Por favor ingrese RUC y fecha del comprobante');
      return;
    }

    setLoading(true);
    try {
      const result = await buscarTimbrado(formData.ruc, formData.fecha_comprobante);

      if (result.encontrado) {
        // Normalize to a list of candidates whether the backend found one or several.
        const candidatos = result.timbrados && result.timbrados.length > 0
          ? result.timbrados
          : [{
              numero_timbrado: result.numero_timbrado,
              nombre_razon_social: result.nombre_razon_social,
              fecha_inicio: result.fecha_inicio,
              fecha_fin: result.fecha_fin,
            }];

        setTimbradoInfo(result);
        setShowTimbradoForm(false);
        setTimbradoOptions(candidatos);

        // Default to the first candidate; the user can still change it below.
        const primero = candidatos[0];
        setFormData(prev => ({
          ...prev,
          numero_timbrado: primero.numero_timbrado,
          nombre_razon_social: primero.nombre_razon_social || prev.nombre_razon_social,
        }));

        if (candidatos.length > 1) {
          toast.info('Se encontraron varios timbrados vigentes para esta fecha. Puede elegir cuál usar.');
        } else {
          toast.success('Timbrado encontrado correctamente');
        }
      } else {
        await prepararTimbradoNuevo();
      }
    } catch (error) {
      console.error('Error buscando timbrado:', error);
      await prepararTimbradoNuevo();
    } finally {
      setLoading(false);
    }
  };

  const handleSelectTimbrado = (numeroTimbrado) => {
    if (numeroTimbrado === '__nuevo__') {
      prepararTimbradoNuevo();
      return;
    }
    const seleccionado = (timbradoOptions || []).find(t => t.numero_timbrado === numeroTimbrado);
    setFormData(prev => ({
      ...prev,
      numero_timbrado: numeroTimbrado,
      nombre_razon_social: seleccionado?.nombre_razon_social || prev.nombre_razon_social,
    }));
  };

  const prepararTimbradoNuevo = async () => {
    setTimbradoInfo({ encontrado: false });
    setTimbradoOptions(null);
    setShowTimbradoForm(true);
    setFormData(prev => ({
      ...prev,
      numero_timbrado: '',
      nombre_razon_social: '',
      fecha_inicio_vigencia: '',
      fecha_fin_vigencia: '',
    }));

    try {
      const contribuyente = await consultarContribuyente(formData.ruc);
      setFormData(prev => ({
        ...prev,
        nombre_razon_social: (contribuyente.razonSocial || '').toUpperCase(),
      }));
      toast.info('No se encontró timbrado. Se completó la razón social automáticamente.');
    } catch (error) {
      console.error('Error consultando contribuyente:', error);
      toast.info('No se encontró timbrado. Complete los datos para registrarlo.');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.numero_timbrado) {
      toast.error(timbradoOptions ? 'Debe seleccionar un timbrado' : 'Debe ingresar un número de timbrado');
      return;
    }

    // Validate timbrado fields if it's a new timbrado
    if (showTimbradoForm) {
      if (!formData.nombre_razon_social) {
        toast.error('Debe ingresar la razón social');
        return;
      }
      if (!formData.fecha_inicio_vigencia || !formData.fecha_fin_vigencia) {
        toast.error('Debe completar las fechas de vigencia del timbrado');
        return;
      }
    }

    setLoading(true);
    try {
      // Register timbrado first if it's new
      if (showTimbradoForm) {
        await registrarTimbrado({
          ruc: formData.ruc,
          numero_timbrado: formData.numero_timbrado,
          nombre_razon_social: formData.nombre_razon_social,
          fecha_inicio: formData.fecha_inicio_vigencia,
          fecha_fin: formData.fecha_fin_vigencia,
        });
        toast.success('Timbrado registrado correctamente');
      }

      // Then save comprobante - convert numeric strings to integers
      const comprobanteData = {
        tipo_registro: '2', // Always set to 2 (Purchases/Libro de Compras)
        tipo_identificacion: '1', // Default to RUC
        identificador: formData.ruc,
        nombre_razon_social: formData.nombre_razon_social,
        tipo_comprobante: formData.tipo_comprobante,
        fecha_comprobante: formData.fecha_comprobante,
        nro_timbrado: formData.numero_timbrado,
        numero_comprobante: formData.numero_comprobante,
        monto_gravado_10: parseInt(formData.monto_gravado_10) || 0,
        monto_gravado_5: parseInt(formData.monto_gravado_5) || 0,
        monto_exento: parseInt(formData.monto_exento) || 0,
        monto_total: parseInt(formData.monto_total) || 0,
        imputa_iva: formData.imputa_iva,
        imputa_ire: formData.imputa_ire,
        imputa_irp_rsp: formData.imputa_irp_rsp,
        condicion_operacion: '1', // Contado (Cash)
        moneda_extranjera: 'N', // No foreign currency
      };
      await crearComprobante(comprobanteData);
      toast.success('Comprobante guardado correctamente');
      
      // Reset form for next entry
      setFormData({ ...INITIAL_FORM_DATA });
      setShowTimbradoForm(false);
      setTimbradoInfo(null);
      setTimbradoOptions(null);
      
      // Focus on RUC field for next entry
      setTimeout(() => {
        rucInputRef.current?.focus();
      }, 100);
    } catch (error) {
      console.error('Error guardando comprobante:', error);
      toast.error(error.response?.data?.error || 'Error al guardar comprobante');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="comprobante-form">
      <div className="card shadow-sm">
        <div className="card-header bg-primary text-white">
          <h4 className="mb-0">Nuevo Comprobante</h4>
        </div>
        <div className="card-body">
          <form onSubmit={handleSubmit}>
            {/* Timbrado Lookup Section */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card bg-light">
                  <div className="card-body">
                    <h5 className="card-title">Búsqueda de Timbrado</h5>
                    <div className="row">
                      <div className="col-md-4">
                        <label className="form-label">RUC (sin DV) *</label>
                        <input
                          type="text"
                          className="form-control"
                          name="ruc"
                          value={formData.ruc}
                          onChange={handleChange}
                          ref={rucInputRef}
                          required
                        />
                      </div>
                      <div className="col-md-4">
                        <label className="form-label">Fecha Comprobante *</label>
                        <DateInput
                          className="form-control"
                          name="fecha_comprobante"
                          value={formData.fecha_comprobante}
                          onChange={handleChange}
                          required
                        />
                      </div>
                      <div className="col-md-4 align-self-end">
                        <button
                          type="button"
                          className="btn btn-info w-100"
                          onClick={handleBuscarTimbrado}
                          disabled={loading}
                        >
                          <i className="bi bi-search"></i> Buscar Timbrado
                        </button>
                      </div>
                    </div>

                    {timbradoOptions && (
                      <div className="alert alert-success mt-3">
                        <strong>
                          {timbradoOptions.length > 1
                            ? 'Se encontraron varios timbrados vigentes para esta fecha:'
                            : 'Timbrado Encontrado:'}
                        </strong>
                        <select
                          className="form-control mt-2"
                          value={formData.numero_timbrado}
                          onChange={(e) => handleSelectTimbrado(e.target.value)}
                        >
                          {timbradoOptions.map((t) => (
                            <option key={t.numero_timbrado} value={t.numero_timbrado}>
                              {t.numero_timbrado} — {t.nombre_razon_social} ({t.fecha_inicio} - {t.fecha_fin})
                            </option>
                          ))}
                          <option value="__nuevo__">-- Ingresar otro timbrado --</option>
                        </select>
                      </div>
                    )}

                    {showTimbradoForm && (
                      <div className="alert alert-warning mt-3">
                        <strong>Registrar Nuevo Timbrado:</strong>
                        <div className="row mt-3">
                          <div className="col-md-12 mb-2">
                            <label className="form-label">Número de Timbrado *</label>
                            <input
                              type="text"
                              className="form-control"
                              name="numero_timbrado"
                              value={formData.numero_timbrado}
                              onChange={handleChange}
                              placeholder="Ej: 12345678"
                              required
                            />
                          </div>
                          <div className="col-md-12 mb-2">
                            <label className="form-label">Nombre o Razón Social *</label>
                            <input
                              type="text"
                              className="form-control"
                              name="nombre_razon_social"
                              value={formData.nombre_razon_social}
                              onChange={handleChange}
                              placeholder="Ej: EMPRESA S.A."
                              required
                            />
                          </div>
                          <div className="col-md-6 mb-2">
                            <label className="form-label">Fecha Inicio Vigencia *</label>
                            <DateInput
                              name="fecha_inicio_vigencia"
                              value={formData.fecha_inicio_vigencia}
                              onChange={handleChange}
                              required
                            />
                          </div>
                          <div className="col-md-6 mb-2">
                            <label className="form-label">Fecha Fin Vigencia * (Auto-calculada)</label>
                            <DateInput
                              name="fecha_fin_vigencia"
                              value={formData.fecha_fin_vigencia}
                              onChange={handleChange}
                              required
                            />
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>

            {/* Comprobante Details */}
            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="form-label">Tipo de Comprobante *</label>
                <select
                  className="form-control"
                  name="tipo_comprobante"
                  value={formData.tipo_comprobante}
                  onChange={handleChange}
                  required
                >
                  <option value="109">109 - FACTURA</option>
                  <option value="110">110 - NOTA DE CRÉDITO</option>
                  <option value="111">111 - NOTA DE DÉBITO</option>
                  <option value="103">103 - BOLETA DE VENTA</option>
                </select>
              </div>

              <div className="col-md-6 mb-3">
                <label className="form-label">Número de Comprobante *</label>
                <input
                  type="text"
                  className="form-control"
                  name="numero_comprobante"
                  value={formData.numero_comprobante}
                  onChange={handleChange}
                  placeholder="001-001-0000001"
                  required
                />
              </div>
            </div>

            {/* Amounts */}
            <div className="row">
              <div className="col-md-3 mb-3">
                <label className="form-label">Monto Gravado 10%</label>
                <input
                  type="number"
                  className="form-control"
                  name="monto_gravado_10"
                  value={formData.monto_gravado_10}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Monto Gravado 5%</label>
                <input
                  type="number"
                  className="form-control"
                  name="monto_gravado_5"
                  value={formData.monto_gravado_5}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Monto Exento</label>
                <input
                  type="number"
                  className="form-control"
                  name="monto_exento"
                  value={formData.monto_exento}
                  onChange={handleChange}
                />
              </div>

              <div className="col-md-3 mb-3">
                <label className="form-label">Monto Total</label>
                <input
                  type="number"
                  className="form-control"
                  name="monto_total"
                  value={formData.monto_total}
                  readOnly
                  style={{ backgroundColor: '#e9ecef' }}
                />
              </div>
            </div>

            {/* Tax Flags */}
            <div className="row">
              <div className="col-md-4 mb-3">
                <label className="form-label">Imputa IVA</label>
                <select
                  className="form-control"
                  name="imputa_iva"
                  value={formData.imputa_iva}
                  onChange={handleChange}
                >
                  <option value="S">Sí</option>
                  <option value="N">No</option>
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Imputa IRE</label>
                <select
                  className="form-control"
                  name="imputa_ire"
                  value={formData.imputa_ire}
                  onChange={handleChange}
                >
                  <option value="S">Sí</option>
                  <option value="N">No</option>
                </select>
              </div>

              <div className="col-md-4 mb-3">
                <label className="form-label">Imputa IRP/RSP</label>
                <select
                  className="form-control"
                  name="imputa_irp_rsp"
                  value={formData.imputa_irp_rsp}
                  onChange={handleChange}
                >
                  <option value="S">Sí</option>
                  <option value="N">No</option>
                </select>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="row mt-4">
              <div className="col-12">
                <button
                  type="submit"
                  className="btn btn-primary me-2"
                  disabled={loading || !timbradoInfo || (!!timbradoOptions && !formData.numero_timbrado)}
                >
                  <i className="bi bi-save"></i> Guardar Comprobante
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => navigate('/')}
                >
                  <i className="bi bi-x-circle"></i> Cancelar
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ComprobanteForm;
